import { useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown, GripVertical, Plus } from "lucide-react";

import { BoardFeatureRow } from "@/presentation/components/board/board-feature-row";
import {
  BOARD_MIN_WIDTH,
  SORTABLE_COLUMN_KEYS,
  type BoardColumn,
  type BoardSort,
} from "@/presentation/data/board";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import type {
  BoardFeatureRow as BoardFeature,
  MilestoneRow,
  MvpComponentRow,
  StakeholderRow,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

interface BoardFeatureTableProps {
  features: BoardFeature[];
  orderedColumns: BoardColumn[];
  gridTemplate: string;
  onReorderColumns: (keys: string[]) => void;
  sort: BoardSort | null;
  onToggleSort: (key: string) => void;
  components: MvpComponentRow[];
  componentsById: Map<string, MvpComponentRow>;
  milestones: MilestoneRow[];
  stakeholders: StakeholderRow[];
  workstreams: WorkstreamRow[];
  onPatch: (id: string, patch: Partial<BoardFeature>) => void;
  onOpen: (id: string) => void;
  draggedId: string | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDropRelative: (targetId: string, position: "before" | "after") => void;
  workstreamCodeByKey: Map<string, string>;
  onAddFeature: (title: string) => void;
  /** view-only: hides the add-item affordance (writes are also gated upstream) */
  readOnly?: boolean;
}

export function BoardFeatureTable({
  features,
  orderedColumns,
  gridTemplate,
  onReorderColumns,
  sort,
  onToggleSort,
  components,
  componentsById,
  milestones,
  stakeholders,
  workstreams,
  onPatch,
  onOpen,
  draggedId,
  onDragStart,
  onDragEnd,
  onDropRelative,
  workstreamCodeByKey,
  onAddFeature,
  readOnly = false,
}: BoardFeatureTableProps) {
  const [draggedCol, setDraggedCol] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<{ key: string; edge: "before" | "after" } | null>(null);

  const reorder = (targetKey: string, edge: "before" | "after") => {
    if (!draggedCol || draggedCol === targetKey) return;
    const keys = orderedColumns.map((c) => c.key).filter((k) => k !== draggedCol);
    const tIdx = keys.indexOf(targetKey);
    keys.splice(edge === "before" ? tIdx : tIdx + 1, 0, draggedCol);
    onReorderColumns(keys);
    setDraggedCol(null);
    setOverCol(null);
  };

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: BOARD_MIN_WIDTH }}>
        {/* column header */}
        <div
          className="border-border bg-card grid w-full items-center border-b"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <div className="eyebrow group bg-card sticky left-0 z-[2] flex items-center px-3 py-2 shadow-[1px_0_0_var(--border)]">
            <button
              type="button"
              onClick={() => onToggleSort("item")}
              className="hover:text-foreground flex items-center gap-1 transition-colors"
              title="Sort by code"
            >
              Item
              <SortIcon active={sort?.key === "item"} dir={sort?.dir ?? null} />
            </button>
          </div>
          {orderedColumns.map((c) => (
            <div
              key={c.key}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                setDraggedCol(c.key);
              }}
              onDragEnd={() => {
                setDraggedCol(null);
                setOverCol(null);
              }}
              onDragOver={(e) => {
                if (!draggedCol || draggedCol === c.key) return;
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                setOverCol({
                  key: c.key,
                  edge: e.clientX < r.left + r.width / 2 ? "before" : "after",
                });
              }}
              onDragLeave={() => setOverCol((o) => (o?.key === c.key ? null : o))}
              onDrop={(e) => {
                e.preventDefault();
                reorder(c.key, overCol?.edge ?? "before");
              }}
              className={cn(
                "eyebrow group border-border/60 hover:bg-muted/60 relative flex cursor-grab items-center gap-1 border-l px-3 py-2 transition-colors select-none active:cursor-grabbing",
                draggedCol === c.key && "opacity-40"
              )}
              title="Drag to reorder column"
            >
              {overCol?.key === c.key ? (
                <span
                  className={cn(
                    "bg-primary pointer-events-none absolute top-0 bottom-0 z-[1] w-0.5",
                    overCol.edge === "before" ? "-left-px" : "-right-px"
                  )}
                  aria-hidden
                />
              ) : null}
              <GripVertical
                className="text-muted-foreground/50 -ml-1 size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
              {SORTABLE_COLUMN_KEYS.has(c.key) ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSort(c.key);
                  }}
                  // a click toggles sort; column reorder still works by dragging
                  className="hover:text-foreground flex min-w-0 items-center gap-1 transition-colors"
                  title={`Sort by ${c.label}`}
                >
                  <span className="truncate">{c.label}</span>
                  <SortIcon active={sort?.key === c.key} dir={sort?.dir ?? null} />
                </button>
              ) : (
                <span className="truncate">{c.label}</span>
              )}
            </div>
          ))}
        </div>

        {features.map((f) => (
          <BoardFeatureRow
            key={f.id}
            feature={f}
            orderedColumns={orderedColumns}
            gridTemplate={gridTemplate}
            components={components}
            componentsById={componentsById}
            milestones={milestones}
            stakeholders={stakeholders}
            workstreams={workstreams}
            workstreamCodeByKey={workstreamCodeByKey}
            isDragging={draggedId === f.id}
            readOnly={readOnly}
            onPatch={onPatch}
            onOpen={onOpen}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDropRelative={onDropRelative}
          />
        ))}

        {readOnly ? null : <AddItemRow onAdd={onAddFeature} />}
      </div>
    </div>
  );
}

/** Sort affordance: faint neutral chevrons until active, then a directional arrow. */
function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" | null }) {
  if (active) {
    return dir === "desc" ? (
      <ChevronDown className="text-primary size-3 shrink-0" aria-hidden />
    ) : (
      <ChevronUp className="text-primary size-3 shrink-0" aria-hidden />
    );
  }
  return (
    <ChevronsUpDown
      className="text-muted-foreground/40 size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
      aria-hidden
    />
  );
}

function AddItemRow({ onAdd }: { onAdd: (title: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const commit = () => {
    const title = value.trim();
    if (title) {
      onAdd(title);
      setValue("");
      // keep the input open + focused for rapid entry (Monday-style)
    }
  };

  return (
    <div className="border-border border-t">
      <div className="sticky left-0 w-[min(100%,560px)] p-1.5">
        {editing ? (
          <input
            autoFocus
            type="text"
            value={value}
            placeholder="Add a feature and press Enter…"
            className="focus:border-ring focus:bg-background border-border/60 h-8 w-full rounded-sm border bg-transparent px-2 text-[13px] outline-none"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              } else if (e.key === "Escape") {
                setValue("");
                setEditing(false);
              }
            }}
            onBlur={() => {
              commit();
              setEditing(false);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/40 flex h-8 w-full items-center gap-1.5 rounded-sm px-2 text-left text-[13px] transition"
          >
            <Plus className="size-3.5" />
            Add item
          </button>
        )}
      </div>
    </div>
  );
}
